import { AvatarDropdown, AvatarName, Footer } from '@/components';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/user';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import type { RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import React from 'react';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import { ConfigProvider, App } from 'antd';
import theme from './theme';
import { setLocale } from 'umi';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      return msg.data;
    } catch (error) {
      history.push(loginPath);
    }
    return undefined;
  };

  // 如果是Google回调页面，不要立即获取用户信息
  const { location } = history;
  if (location.pathname !== loginPath && !location.pathname.startsWith('/auth/google/callback')) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState }) => {
  return {
    actionsRender: undefined,
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，且不在登录页或回调页，则重定向到 login
      if (
        !initialState?.currentUser &&
        location.pathname !== loginPath &&
        !location.pathname.startsWith('/auth/google/callback')
      ) {
        history.push(loginPath);
      }
    },
    links: isDev ? [] : [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => children,
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request = {
  ...errorConfig,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const errorHandler = (error: any) => {
  const { response } = error;

  // 对于OAuth配置未找到的错误，不显示任何提示
  if (
    response &&
    response.data &&
    response.data.error === 'No specified OAuth configuration found'
  ) {
    // 静默返回，不显示错误
    return { success: true, data: { enabled: false } };
  }

  // 其他错误的正常处理
  if (response && response.status) {
    // 处理其他HTTP错误...
  }

  return Promise.reject(error);
};

// 在应用初始化时
export function rootContainer(container) {
  return (
    <ConfigProvider theme={theme}>
      <App message={{ maxCount: 3 }} notification={{ maxCount: 3 }}>
        {container}
      </App>
    </ConfigProvider>
  );
}

// 设置默认语言为英文
export function render(oldRender) {
  // 强制设置为英文
  setLocale('en-US', false);
  oldRender();
}
